import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { RunWithDetails } from '../../types'
import { FullPlanDocument } from './templates/FullPlanDocument'
import { CompactCardDocument } from './templates/CompactCardDocument'
import { PDFGenerationOptions, PDFExportError } from './types'

/**
 * Generate a full plan PDF from run data
 * Replaces the HTML-based generateFullPlan function
 */
export async function generateFullPlanPDF(
  run: RunWithDetails,
  options: PDFGenerationOptions = { format: 'full' }
): Promise<Blob> {
  try {
    if (!run) {
      throw new Error('Run data is required for PDF generation')
    }

    if (!run.segments || run.segments.length === 0) {
      throw new Error('Run must have at least one segment to generate PDF')
    }

    // Create the PDF document
    const document = <FullPlanDocument run={run} />
    
    // Generate PDF blob
    const blob = await pdf(document).toBlob()
    
    if (!blob) {
      throw new Error('Failed to generate PDF blob')
    }

    return blob
  } catch (error) {
    const pdfError: PDFExportError = {
      type: 'generation',
      message: `Failed to generate full plan PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
      details: error
    }
    
    console.error('PDF Generation Error:', pdfError)
    throw new Error(pdfError.message)
  }
}

/**
 * Generate a compact pace card PDF from run data
 * Replaces the HTML-based generateCompactCard function
 */
export async function generateCompactCardPDF(
  run: RunWithDetails,
  options: PDFGenerationOptions = { format: 'compact' }
): Promise<Blob> {
  try {
    if (!run) {
      throw new Error('Run data is required for PDF generation')
    }

    if (!run.segments || run.segments.length === 0) {
      throw new Error('Run must have at least one segment to generate PDF')
    }

    // Create the PDF document
    const document = <CompactCardDocument run={run} />
    
    // Generate PDF blob
    const blob = await pdf(document).toBlob()
    
    if (!blob) {
      throw new Error('Failed to generate PDF blob')
    }

    return blob
  } catch (error) {
    const pdfError: PDFExportError = {
      type: 'generation',
      message: `Failed to generate compact card PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
      details: error
    }
    
    console.error('PDF Generation Error:', pdfError)
    throw new Error(pdfError.message)
  }
}

/**
 * Generate PDF based on format type
 * Unified interface for both PDF types
 */
export async function generatePDF(
  run: RunWithDetails,
  format: 'full' | 'compact',
  options?: Partial<PDFGenerationOptions>
): Promise<Blob> {
  const fullOptions: PDFGenerationOptions = {
    format,
    includeNotes: true,
    includeAidStations: true,
    includeNutritionBreakdown: true,
    optimize: 'print',
    ...options
  }

  switch (format) {
    case 'full':
      return generateFullPlanPDF(run, fullOptions)
    case 'compact':
      return generateCompactCardPDF(run, fullOptions)
    default:
      throw new Error(`Unsupported PDF format: ${format}`)
  }
}

/**
 * Download a PDF file with proper filename
 * Helper function to handle browser download
 */
export function downloadPDF(blob: Blob, filename: string) {
  try {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`
    
    // Append to body, click, and remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the URL object
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
    throw new Error(`Failed to download PDF: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Generate and download full plan PDF
 * Convenience function combining generation and download
 */
export async function downloadFullPlanPDF(run: RunWithDetails): Promise<void> {
  const blob = await generateFullPlanPDF(run)
  const filename = `${run.title.replace(/\s+/g, '_')}_full_plan.pdf`
  downloadPDF(blob, filename)
}

/**
 * Generate and download compact card PDF
 * Convenience function combining generation and download
 */
export async function downloadCompactCardPDF(run: RunWithDetails): Promise<void> {
  const blob = await generateCompactCardPDF(run)
  const filename = `${run.title.replace(/\s+/g, '_')}_pace_card.pdf`
  downloadPDF(blob, filename)
}

/**
 * Print PDF directly in browser
 * Opens PDF in new window for printing
 */
export async function printPDF(blob: Blob, title: string = 'UltraPlan Export'): Promise<void> {
  try {
    const url = URL.createObjectURL(blob)
    const printWindow = window.open(url, '_blank', 'width=800,height=600')
    
    if (!printWindow) {
      throw new Error('Unable to open print window. Please check your popup blocker settings.')
    }
    
    // Set window title and focus
    printWindow.document.title = title
    printWindow.focus()
    
    // Wait for PDF to load, then trigger print
    printWindow.addEventListener('load', () => {
      setTimeout(() => {
        printWindow.print()
      }, 500)
    })
    
    // Clean up URL when window closes
    const checkClosed = setInterval(() => {
      if (printWindow.closed) {
        clearInterval(checkClosed)
        URL.revokeObjectURL(url)
      }
    }, 1000)
    
  } catch (error) {
    console.error('Print failed:', error)
    throw new Error(`Failed to print PDF: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Generate PDF data URL for preview
 * Returns a data URL that can be used in iframe or object elements
 */
export async function generatePDFDataUrl(
  run: RunWithDetails,
  format: 'full' | 'compact'
): Promise<string> {
  try {
    const blob = await generatePDF(run, format)
    return URL.createObjectURL(blob)
  } catch (error) {
    console.error('PDF data URL generation failed:', error)
    throw new Error(`Failed to generate PDF preview: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Validate run data before PDF generation
 * Checks for required fields and data integrity
 */
export function validateRunForPDF(run: RunWithDetails): { isValid: boolean; errors: string[] } {
  const errors: string[] = []
  
  if (!run) {
    errors.push('Run data is missing')
    return { isValid: false, errors }
  }
  
  if (!run.title || run.title.trim() === '') {
    errors.push('Run title is required')
  }
  
  if (!run.segments || run.segments.length === 0) {
    errors.push('Run must have at least one segment')
  }
  
  // Validate segments have required data
  if (run.segments) {
    run.segments.forEach((segment, index) => {
      if (!segment.distance_km || segment.distance_km <= 0) {
        errors.push(`Segment ${index + 1} must have a valid distance`)
      }
    })
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Get PDF generation progress
 * For future implementation of progress indicators
 */
export interface PDFGenerationProgress {
  stage: 'validating' | 'processing' | 'rendering' | 'complete'
  percentage: number
  message: string
}

/**
 * Generate PDF with progress callback
 * Future enhancement for progress tracking
 */
export async function generatePDFWithProgress(
  run: RunWithDetails,
  format: 'full' | 'compact',
  onProgress?: (progress: PDFGenerationProgress) => void
): Promise<Blob> {
  if (onProgress) {
    onProgress({ stage: 'validating', percentage: 0, message: 'Validating run data...' })
  }
  
  const validation = validateRunForPDF(run)
  if (!validation.isValid) {
    throw new Error(`Invalid run data: ${validation.errors.join(', ')}`)
  }
  
  if (onProgress) {
    onProgress({ stage: 'processing', percentage: 25, message: 'Processing segments and nutrition data...' })
  }
  
  if (onProgress) {
    onProgress({ stage: 'rendering', percentage: 50, message: 'Rendering PDF document...' })
  }
  
  const blob = await generatePDF(run, format)
  
  if (onProgress) {
    onProgress({ stage: 'complete', percentage: 100, message: 'PDF generation complete!' })
  }
  
  return blob
}